/**
 * dailyRead — synthesize open notches into a per-market trading read.
 *
 * Groups live (in-flight) notches by underlying market, classifies each
 * along the direction axis (bull/bear/none) and vol axis (long-vol/short-
 * vol/none), then picks an alignment state and generates a plain-English
 * synthesis + primary trade + size hint.
 *
 * The synthesis rules are the codified version of the Playbook — same
 * logic a reader would apply by hand.
 */

import {
  ScorecardPayload, ScorecardModel, ScorecardOutcome,
} from './notchesScorecardService';
import { marketOf } from './notchConfluence';

export type Direction = 'bull' | 'bear' | 'none';
export type VolBias = 'long-vol' | 'short-vol' | 'none';

export type Alignment =
  | 'aligned-bullish'
  | 'aligned-bullish-premium-seller'
  | 'aligned-bearish'
  | 'aligned-bearish-long-vol'
  | 'coiled-spring'          // GEX POS + IV LOW: short compress, long expand
  | 'vol-only'               // only GEX/IV notches, no direction call
  | 'contradicting'          // direction notches disagree
  | 'single';                // one notch, no confluence

export interface OpenNotchView {
  model: ScorecardModel;
  symbol: string;
  tag: string;
  reading: string;
  outcome: ScorecardOutcome;
  daysAgo: number;
  horizonDays: number;
  direction: Direction;
  vol: VolBias;
  cellHitPct: number | null;   // this notch's own cell hit rate, for inline proof
  cellN: number;               // number of resolved readings behind that hit rate
}

// Notches Desk covers these symbols today. Sector ETFs (XLE/XLF/XLK/XLV) and
// NQ/MNQ do not have desk coverage yet — the overview flags this so readers
// aren't sent to a page they can't use.
const NOTCHES_DESK_SYMBOLS = new Set([
  'SPX', 'SPY', 'QQQ', 'IWM', 'ES', 'MES', 'GC', 'CL',
]);

// The tradable proxy from a market name — used to check desk coverage.
function proxyForMarket(market: string): string {
  switch (market) {
    case 'S&P 500':      return 'SPY';
    case 'Nasdaq-100':   return 'QQQ';
    case 'Russell 2000': return 'IWM';
    case 'Gold':         return 'GC';
    case 'Crude Oil':    return 'CL';
    default:             return market;
  }
}

export interface DailyReadEntry {
  market: string;
  notches: OpenNotchView[];
  alignment: Alignment;
  headline: string;          // "Bearish + vol expanding"
  marketContext: string;     // NEW — scene-setter sentence(s)
  synthesis: string;         // Analysis Summary — one paragraph, soft "a trader might..." tone
  thingsToWatch: string[];   // NEW — bullet-point confirmations / invalidations / dates
  primaryTrade: string;      // Play — "long QQQ puts 15-30 DTE ~40-delta"
  size: string;              // "1.5× · aligned confluence lifts weak single cells"
  coveredByDesk: boolean;    // true if Notches Desk has this market's proxy
  convictionRank: number;    // 0 = highest (aligned dual-dim), 5 = lowest (contradicting)
  accent: 'bull' | 'bear' | 'mixed' | 'neutral';
}

// ---- classification ------------------------------------------------------

function classifyDirection(model: ScorecardModel, tag: string): Direction {
  if (model === 'COT') {
    if (tag.includes('LONG')) return 'bull';
    if (tag.includes('SHORT')) return 'bear';
  }
  if (model === 'AVWAP') {
    if (tag === 'AVWAP_HELD') return 'bull';
    if (tag === 'AVWAP_BROKE') return 'bear';
    // AVWAP_TEST while open = neutral (waiting)
  }
  return 'none';
}

function classifyVol(model: ScorecardModel, tag: string): VolBias {
  if (model === 'GEX') {
    if (tag.startsWith('NEG_GEX')) return 'long-vol';
    if (tag.startsWith('POS_GEX')) return 'short-vol';
  }
  if (model === 'IV') {
    if (tag === 'IVR_HIGH') return 'short-vol';   // sell premium
    if (tag === 'IVR_LOW')  return 'long-vol';    // buy premium
  }
  return 'none';
}

// ---- alignment picker ---------------------------------------------------

function pickAlignment(notches: OpenNotchView[]): Alignment {
  const dirs = notches.map((n) => n.direction).filter((d) => d !== 'none');
  const vols = notches.map((n) => n.vol).filter((v) => v !== 'none');

  const uniqDirs = Array.from(new Set(dirs));
  const uniqVols = Array.from(new Set(vols));

  const hasBull = uniqDirs.includes('bull');
  const hasBear = uniqDirs.includes('bear');
  const hasLongVol = uniqVols.includes('long-vol');
  const hasShortVol = uniqVols.includes('short-vol');

  // Single notch → simple case
  if (notches.length === 1) return 'single';

  // Directional conflict
  if (hasBull && hasBear) return 'contradicting';

  // Vol coiled spring: dealers grind now (POS_GEX), premium is cheap for
  // longer horizon (IV LOW) → short-term compression + longer expansion
  if (hasShortVol && hasLongVol && uniqDirs.length === 0) return 'coiled-spring';

  // Aligned bullish variants
  if (hasBull) {
    if (hasShortVol) return 'aligned-bullish-premium-seller';
    return 'aligned-bullish';
  }

  // Aligned bearish variants
  if (hasBear) {
    if (hasLongVol) return 'aligned-bearish-long-vol';
    return 'aligned-bearish';
  }

  // No direction, only vol → vol-only regime read
  return 'vol-only';
}

// ---- shared "Things to Watch" builders ---------------------------------
// Every alignment gets 3-4 watch bullets composed from the notches + horizon
// data we already have. Keeps the marketing prose grounded in real data.

function minDaysLeft(notches: OpenNotchView[]): number {
  return notches.reduce(
    (m, n) => Math.min(m, Math.max(0, n.horizonDays - n.daysAgo)),
    Number.POSITIVE_INFINITY,
  );
}

// Compact "COT 58% · GEX 44%" phrase from the current notches
function trackRecordPhrase(notches: OpenNotchView[]): string {
  return notches
    .filter((n) => n.cellHitPct != null)
    .map((n) => `${n.model} ${n.cellHitPct!.toFixed(0)}%`)
    .join(' · ');
}

function watchResolution(notches: OpenNotchView[]): string {
  const d = minDaysLeft(notches);
  if (!Number.isFinite(d) || d <= 0) return `Read is at the close of its scoring window — the outcome posts today.`;
  if (d === 1) return `Read resolves in 1 trading day. Watch the close for hit-or-miss.`;
  return `Read resolves in ${d} trading days. Watch the close of each session for whether the story holds.`;
}

function watchTrackRecord(notches: OpenNotchView[]): string {
  const phrase = trackRecordPhrase(notches);
  if (!phrase) return `Track records for these cells are still building.`;
  return `Track record on this symbol: ${phrase} — modest individual cells; the conviction comes from alignment, not any single number.`;
}

// ---- headline + synthesis + trade per alignment -------------------------

// Priority (lower = higher conviction) — used to rank hero placement + sort order.
const CONVICTION_RANK: Record<Alignment, number> = {
  'aligned-bearish-long-vol':       0,
  'aligned-bullish-premium-seller': 0,
  'aligned-bullish':                1,
  'aligned-bearish':                1,
  'coiled-spring':                  2,
  'vol-only':                       3,
  'single':                         4,
  'contradicting':                  5,
};

function buildEntry(market: string, notches: OpenNotchView[]): DailyReadEntry {
  const alignment = pickAlignment(notches);
  const tradable = marketToTradable(market);
  const coveredByDesk = NOTCHES_DESK_SYMBOLS.has(proxyForMarket(market));
  const convictionRank = CONVICTION_RANK[alignment];
  const base = { market, notches, alignment, coveredByDesk, convictionRank };

  switch (alignment) {
    case 'aligned-bullish-premium-seller':
      return { ...base,
        headline: 'Bullish — and premium is expensive',
        marketContext:
          `${market} is in a regime where two of our independent models are pointing the same direction: positioning is leaning long AND options premium is elevated. When these two align, the setup historically favors sellers of premium rather than buyers of direction.`,
        synthesis:
          `Institutional positioning is pointing up, and options premium on ${tradable} is high relative to its own history. A trader might read this as a "collect the rich premium while the underlying grinds higher" setup.`,
        thingsToWatch: [
          `Confirmation: ${tradable} continues to grind higher without a volatility shock reversing the IV read.`,
          `Invalidation: a decisive down-move or IV spike would flip both models' calls before the window closes.`,
          watchTrackRecord(notches),
          watchResolution(notches),
        ],
        primaryTrade: `a put credit spread on ${tradable}, 20–45 DTE, sold at 25–30 delta; add a call spread on top if they want the iron condor`,
        size: '1.5× typical size — aligned confluence is the highest-conviction cell',
        accent: 'bull',
      };
    case 'aligned-bullish':
      return { ...base,
        headline: 'Bullish setup',
        marketContext:
          `${market} is showing directional agreement between at least two of our models — the read is that the underlying is more likely to head higher than lower over the next few weeks.`,
        synthesis:
          `Institutional positioning is pointing up on ${tradable}. A trader might use defined-risk long-delta structures matched to the model's own horizon.`,
        thingsToWatch: [
          `Confirmation: ${tradable} closes higher over the next few sessions.`,
          `Invalidation: a break of the recent low would flip the positioning read.`,
          watchTrackRecord(notches),
          watchResolution(notches),
        ],
        primaryTrade: `a put credit spread or long calls on ${tradable}, 30–45 DTE, 30-delta short strike`,
        size: '1× typical size — one dimension confirming (direction only)',
        accent: 'bull',
      };
    case 'aligned-bearish-long-vol':
      return { ...base,
        headline: 'Bearish — and volatility is expanding',
        marketContext:
          `${market} is in a two-dimensional bearish read: institutions are positioned short AND dealer hedging should amplify (not dampen) intraday moves. Both the direction call and the vol call point at the same trade — a rare configuration.`,
        synthesis:
          `Institutional positioning is pointing down on ${tradable}, and dealer positioning says intraday moves should amplify (not dampen) over the next week. A trader might read this as a "short-delta AND long-vol" moment — both dimensions favor the same instrument.`,
        thingsToWatch: [
          `Confirmation: ${tradable} closes lower over the next few sessions with a rising VIX.`,
          `Invalidation: a strong rally that pushes ${tradable} back above the recent gamma flip would defuse the long-vol thesis first, then the short thesis.`,
          watchTrackRecord(notches),
          watchResolution(notches),
        ],
        primaryTrade: `long puts on ${tradable}, 15–30 DTE, ~40-delta — captures both the direction call and the vol call in one leg`,
        size: '1.5× typical size — two dimensions agreeing is rare',
        accent: 'bear',
      };
    case 'aligned-bearish':
      return { ...base,
        headline: 'Bearish setup',
        marketContext:
          `${market} is showing directional agreement toward the downside across at least two of our models over the coming weeks.`,
        synthesis:
          `Institutional positioning is pointing down on ${tradable}. A trader might use short-delta structures matched to the model's own horizon.`,
        thingsToWatch: [
          `Confirmation: ${tradable} closes lower over the next few sessions.`,
          `Invalidation: a break of the recent high would flip the positioning read.`,
          watchTrackRecord(notches),
          watchResolution(notches),
        ],
        primaryTrade: `a put debit spread or long puts on ${tradable}, 30–45 DTE, 30-delta long strike`,
        size: '1× typical size — one dimension confirming (direction only)',
        accent: 'bear',
      };
    case 'coiled-spring':
      return { ...base,
        headline: 'Coiled spring — quiet now, expansion likely',
        marketContext:
          `${market} is in a two-horizon vol configuration: dealer gamma expects the short term to grind tight, but longer-dated options are cheap and history says vol expands from levels this low. Both can be right at different scales.`,
        synthesis:
          `Dealer gamma says the next few days should grind tight, but longer-dated options are cheap and history says vol expands from levels this low. Both can be right on different horizons — a trader might sell the front week and buy the back month.`,
        thingsToWatch: [
          `Confirmation of the front-vol read: ${tradable} stays inside a tight range for the next 5 sessions.`,
          `Confirmation of the back-vol read: IV Rank stays low OR pushes higher as premium reprices for a bigger move.`,
          `Invalidation: an immediate vol spike (VIX >20) collapses the diagonal into a single-directional trade.`,
          watchResolution(notches),
        ],
        primaryTrade: `two legs: an iron condor on ${tradable} 5–15 DTE (short front vol) plus a long straddle 30–60 DTE (long back vol), half-size each`,
        size: '0.5× typical size on each leg — complex structure, small samples on both cells',
        accent: 'mixed',
      };
    case 'vol-only':
      return volOnlyEntry(base, tradable, notches);
    case 'contradicting':
      return { ...base,
        headline: 'Models disagree on direction',
        marketContext:
          `${market} has directional readings pointing both ways within the same week. Positioning says one thing, price action says another. Wait for one side to resolve.`,
        synthesis:
          `One model calls up, another calls down on ${tradable} within the same week. Direction is unresolved. A trader might wait for one reading to resolve, or take a delta-neutral vol trade if the vol dimension is decisive.`,
        thingsToWatch: [
          `Which model closes first — the earlier resolution likely wins the tiebreaker.`,
          `Whether a vol regime (GEX or IV) is decisive independent of direction — a delta-neutral trade could still make sense.`,
          `Any external catalyst (Fed, earnings, macro release) that would force one side to resolve.`,
          watchResolution(notches),
        ],
        primaryTrade: `wait, or take a delta-neutral vol trade sized small`,
        size: '0× to 0.5× typical — sitting on hands is a position',
        accent: 'neutral',
      };
    case 'single':
      return singleNotchEntry(base, tradable, notches[0]);
  }
}

function volOnlyEntry(
  base: Pick<DailyReadEntry, 'market'|'notches'|'alignment'|'coveredByDesk'|'convictionRank'>,
  tradable: string,
  notches: OpenNotchView[],
): DailyReadEntry {
  const longVol = notches.some((n) => n.vol === 'long-vol');
  const shortVol = notches.some((n) => n.vol === 'short-vol');

  if (longVol && !shortVol) {
    return { ...base,
      headline: 'Vol likely to expand — no direction call yet',
      marketContext:
        `${base.market} has a volatility read pointing higher but no directional reading from our positioning models. The next move should be bigger than the option market is currently pricing — direction, however, is anyone's guess.`,
      synthesis:
        `Vol on ${tradable} is expected to expand over the model's horizon. Direction is unknown, so a trader might use a delta-neutral long-vol structure and let the move happen either way.`,
      thingsToWatch: [
        `Confirmation: intraday ranges start widening OR IV Rank rises meaningfully.`,
        `Invalidation: sideways action continues through the window without a directional break.`,
        watchTrackRecord(notches),
        watchResolution(notches),
      ],
      primaryTrade: `a long straddle or strangle on ${tradable}, 5–15 DTE if GEX-driven, 30–60 DTE if IV LOW-driven`,
      size: '1× typical size — matches the horizon of whichever model fired',
      accent: 'neutral',
    };
  }
  if (shortVol && !longVol) {
    return { ...base,
      headline: 'Vol likely to compress — no direction call yet',
      marketContext:
        `${base.market} is in a regime where our vol models expect grinding tight range — dealers are long gamma or premium is expensive. No directional reading from positioning yet; the trade is on vol crush, not direction.`,
      synthesis:
        `Vol on ${tradable} is expected to compress. Direction is neutral, so a trader might use a delta-neutral premium-seller structure and let vol crush pay them.`,
      thingsToWatch: [
        `Confirmation: intraday ranges stay tight; IV Rank drifts lower.`,
        `Invalidation: a vol spike (VIX >20 or symbol-specific ATR expansion) would flip the read.`,
        watchTrackRecord(notches),
        watchResolution(notches),
      ],
      primaryTrade: `an iron condor or short strangle on ${tradable}, 5–15 DTE if GEX-driven, 20–45 DTE if IV HIGH-driven`,
      size: '1× typical size — matches the horizon of whichever model fired',
      accent: 'neutral',
    };
  }
  return { ...base,
    headline: 'Mixed vol regime',
    marketContext: `${base.market} has vol readings that don't yet align — details vary by model.`,
    synthesis: `Vol readings on ${tradable} are present but mixed — see individual model pages for the detail.`,
    thingsToWatch: [
      `Which vol model resolves first — the earlier hit sets the near-term regime.`,
      watchResolution(notches),
    ],
    primaryTrade: `the individual notch with the strongest cell hit rate (see per-model page)`,
    size: '0.5× typical size',
    accent: 'neutral',
  };
}

function singleNotchEntry(
  base: Pick<DailyReadEntry, 'market'|'notches'|'alignment'|'coveredByDesk'|'convictionRank'>,
  tradable: string, n: OpenNotchView,
): DailyReadEntry {
  const cellRef = ` (cell hit rate: ${n.cellHitPct == null ? '—' : `${n.cellHitPct.toFixed(0)}% over ${n.cellN} readings`})`;
  const notches = [n];
  const commonWatch = (extraFirst?: string) => [
    ...(extraFirst ? [extraFirst] : []),
    watchTrackRecord(notches),
    watchResolution(notches),
  ];

  if (n.model === 'COT' && n.direction === 'bull') {
    return { ...base,
      headline: 'One reading — bullish positioning',
      marketContext: `${base.market} has one directional reading firing right now: leveraged-money positioning is stretched long. No other model is confirming yet, so the trade rides on this cell's own track record.`,
      synthesis: `Hedge-fund positioning is stretched long on ${tradable}${cellRef}. No other model is confirming yet — a trader might treat this as a single-reading setup, sized to the cell's own hit rate.`,
      thingsToWatch: commonWatch(`Confirmation: another model (GEX, IV, or AVWAP) firing bullish on ${tradable} would lift this from single-reading to aligned confluence.`),
      primaryTrade: `a put credit spread or long calls on ${tradable}, 30–45 DTE`,
      size: 'Half to full — size per the COT cell hit rate for this symbol',
      accent: 'bull',
    };
  }
  if (n.model === 'COT' && n.direction === 'bear') {
    return { ...base,
      headline: 'One reading — bearish positioning',
      marketContext: `${base.market} has one directional reading firing right now: leveraged-money positioning is stretched short. No other model is confirming yet, so the trade rides on this cell's own track record.`,
      synthesis: `Hedge-fund positioning is stretched short on ${tradable}${cellRef}. No other model is confirming yet — a trader might treat this as a single-reading setup, sized to the cell's own hit rate.`,
      thingsToWatch: commonWatch(`Confirmation: another model (GEX, IV, or AVWAP) firing bearish on ${tradable} would lift this from single-reading to aligned confluence.`),
      primaryTrade: `a put debit spread or long puts on ${tradable}, 30–45 DTE`,
      size: 'Half to full — size per the COT cell hit rate for this symbol',
      accent: 'bear',
    };
  }
  if (n.model === 'GEX' && n.vol === 'long-vol') {
    return { ...base,
      headline: 'One reading — vol expected to expand',
      marketContext: `${base.market} is in a short-gamma regime — dealer hedging is set to amplify intraday moves for the next several sessions. No directional reading yet; the read is on movement, not direction.`,
      synthesis: `Dealer gamma on ${tradable} is short — intraday moves should amplify${cellRef}. A trader might read this as a "buy the move" setup for the next 5–15 days.`,
      thingsToWatch: commonWatch(`Confirmation: intraday ATR expansion; the VIX pushing up alongside spot movement.`),
      primaryTrade: `a long straddle or strangle on ${tradable}, 5–15 DTE — avoid iron condors here`,
      size: 'Half to full — size per the GEX cell hit rate for this symbol',
      accent: 'neutral',
    };
  }
  if (n.model === 'GEX' && n.vol === 'short-vol') {
    return { ...base,
      headline: 'One reading — vol expected to compress',
      marketContext: `${base.market} is in a long-gamma regime — dealer hedging should dampen intraday moves and keep the range tight for the next several sessions. No directional reading yet; the read is on quiet, not direction.`,
      synthesis: `Dealer gamma on ${tradable} is long — expect grinding tight range${cellRef}. A trader might read this as a "sell the range" setup for the next 5–15 days.`,
      thingsToWatch: commonWatch(`Confirmation: intraday ranges stay small; IV drifts lower.`),
      primaryTrade: `an iron condor or credit spread on ${tradable}, 5–15 DTE — avoid straddles here`,
      size: 'Half to full — size per the GEX cell hit rate for this symbol',
      accent: 'neutral',
    };
  }
  if (n.model === 'IV' && n.vol === 'short-vol') {
    return { ...base,
      headline: 'One reading — premium is expensive',
      marketContext: `${base.market} has IV Rank crossing into the high band — options are pricier than usual relative to the symbol's own history. The trade is on mean reversion, not direction.`,
      synthesis: `Options on ${tradable} are priced high relative to the symbol's own IV history${cellRef}. A trader might read this as a mean-reversion setup and sell premium.`,
      thingsToWatch: commonWatch(`Confirmation: IV Rank drifts back below 50 within the window.`),
      primaryTrade: `a put credit spread, iron condor, or covered call on ${tradable}, 20–45 DTE`,
      size: 'Half to full — size per the IV cell hit rate for this symbol',
      accent: 'neutral',
    };
  }
  if (n.model === 'IV' && n.vol === 'long-vol') {
    return { ...base,
      headline: 'One reading — premium is cheap',
      marketContext: `${base.market} has IV Rank crossing into the low band — options are cheaper than usual relative to the symbol's own history. Historically vol expands from levels this low.`,
      synthesis: `Options on ${tradable} are priced low relative to the symbol's own IV history${cellRef}. A trader who expects a directional move might buy premium; a delta-neutral trader might buy a straddle.`,
      thingsToWatch: commonWatch(`Confirmation: IV Rank rises above 50 within the window (which itself is the outcome test).`),
      primaryTrade: `a long call, long put, or long straddle on ${tradable}, 30–60 DTE`,
      size: 'Half to full — size per the IV cell hit rate for this symbol',
      accent: 'neutral',
    };
  }
  if (n.model === 'AVWAP') {
    return { ...base,
      headline: 'One reading — support level being tested',
      marketContext: `${base.market} is testing the 60-day swing-low AVWAP right now — the level institutions have historically defended. The 5-day close is the tell; there's nothing decisive to trade until the level resolves.`,
      synthesis: `Spot on ${tradable} is testing the 60-day swing-low AVWAP right now. The read resolves in 5 trading days: a close above AVWAP is bullish, a close 2%+ below is bearish. Nothing to trade until the level resolves.`,
      thingsToWatch: [
        `Confirmation of "held": ${tradable} closes above AVWAP in 5 trading days.`,
        `Confirmation of "broke": ${tradable} closes 2%+ below AVWAP in 5 trading days.`,
        watchTrackRecord(notches),
        watchResolution(notches),
      ],
      primaryTrade: `wait 5 trading days for the outcome, then a bull put spread (if it held) or a bear call spread (if it broke)`,
      size: 'Wait for confirmation',
      accent: 'neutral',
    };
  }
  return { ...base,
    headline: `One reading · ${n.model}`,
    marketContext: `${base.market} has a single reading from the ${n.model} model.`,
    synthesis: n.reading,
    thingsToWatch: commonWatch(),
    primaryTrade: `see the ${n.model} page for the specific trade structure`,
    size: '',
    accent: 'neutral',
  };
}

// ---- market → tradable ETF ---------------------------------------------

function marketToTradable(market: string): string {
  switch (market) {
    case 'S&P 500':      return 'SPY';
    case 'Nasdaq-100':   return 'QQQ';
    case 'Russell 2000': return 'IWM';
    case 'Gold':         return 'GLD';
    case 'Crude Oil':    return 'USO';
    case 'Energy':       return 'XLE';
    case 'Financials':   return 'XLF';
    case 'Technology':   return 'XLK';
    case 'Health Care':  return 'XLV';
    default:             return market;
  }
}

// ---- orchestrator -------------------------------------------------------

function daysBetween(a: string, b: string): number {
  return Math.floor(
    (new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86400000
  );
}

// Look up the cell hit rate + n for a notch's specific model+symbol.
// Returns {hitPct: number|null, n: number}. This is the inline "proof"
// number that ends up under each notch on the overview cards.
function findCell(
  data: ScorecardPayload, model: ScorecardModel, symbol: string,
): { hitPct: number | null; n: number } {
  const blocks: Record<ScorecardModel, { aggregates: { symbol: string; hitPct: number | null; n: number }[] }> = {
    COT: data.cot, GEX: data.gex, IV: data.iv, AVWAP: data.avwap,
  };
  const agg = blocks[model].aggregates.find((a) => a.symbol === symbol);
  return { hitPct: agg?.hitPct ?? null, n: agg?.n ?? 0 };
}

/**
 * Build the daily read across all markets with at least one open notch.
 * Returns entries sorted by conviction: aligned confluence first, then
 * coiled/vol-only, then single, contradicting last.
 */
export function buildDailyRead(data: ScorecardPayload): DailyReadEntry[] {
  const todayIso = new Date().toISOString().slice(0, 10);
  const opens: OpenNotchView[] = [];

  const collect = (
    rows: { date: string; symbol: string; tag: string; reading: string; outcome: ScorecardOutcome }[],
    model: ScorecardModel,
    horizonDays: number,
  ) => {
    for (const r of rows) {
      if (r.outcome !== 'open') continue;
      const cell = findCell(data, model, r.symbol);
      opens.push({
        model, symbol: r.symbol, tag: r.tag, reading: r.reading, outcome: r.outcome,
        daysAgo: daysBetween(r.date, todayIso),
        horizonDays,
        direction: classifyDirection(model, r.tag),
        vol: classifyVol(model, r.tag),
        cellHitPct: cell.hitPct,
        cellN: cell.n,
      });
    }
  };
  collect(data.cot.rows, 'COT', data.cot.meta.horizonDays);
  collect(data.gex.rows, 'GEX', data.gex.meta.forwardDays);
  collect(data.iv.rows,  'IV',  data.iv.meta.windowDays);
  collect(data.avwap.rows, 'AVWAP', data.avwap.meta.forwardDays);

  // Group by market
  const byMarket = new Map<string, OpenNotchView[]>();
  for (const n of opens) {
    const m = marketOf(n.symbol);
    const list = byMarket.get(m) || [];
    list.push(n);
    byMarket.set(m, list);
  }

  const entries: DailyReadEntry[] = [];
  for (const [market, notches] of Array.from(byMarket.entries())) {
    entries.push(buildEntry(market, notches));
  }

  entries.sort((a, b) => a.convictionRank - b.convictionRank);
  return entries;
}
